<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import { useOnline } from '@vueuse/core';
  import BranchesService from '@/api/services/branches.service';
  import { StoreSetupService } from '@/api/services/store-setup.service';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import V2SetupStorePreview from '@/components/v2/store-setup/shared/V2SetupStorePreview.vue';
  import V2SetupDeliveryCard from '@/components/v2/store-setup/orders/V2SetupDeliveryCard.vue';
  import V2SetupOrderTypes from '@/components/v2/store-setup/orders/V2SetupOrderTypes.vue';
  import V2SetupOrdersArrival from '@/components/v2/store-setup/orders/V2SetupOrdersArrival.vue';
  import V2SetupPaymentChips from '@/components/v2/store-setup/orders/V2SetupPaymentChips.vue';
  import { flowBar } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import { useGtag } from '@/composition/use-gtag';
  import { useUi } from '@/composition/use-ui';
  import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
  import { todayHoursLabel } from '@/domain/store-setup/opening-hours';
  import {
    initialOrdersForm,
    ordersBlocker,
    ordersPreviewModel,
    toSetupOrdersDto,
  } from '@/domain/store-setup/orders-setup';
  import type {
    IOrdersContext,
    IOrdersForm,
  } from '@/domain/store-setup/orders-setup';
  import {
    branchWeek,
    mergeBranchUpdate,
    ordersConfiguredEvent,
    ordersHelperKey,
  } from '@/domain/store-setup/orders-step';
  import type { TOrdersFormPatch } from '@/domain/store-setup/orders-step';
  import {
    isOrdersAlreadySet,
    setupErrorKey,
  } from '@/domain/store-setup/setup-errors';
  import { storeInitials } from '@/domain/store-setup/store-identity';
  import { defaultBrand } from '@/domain/storefront/storefront-colors';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IBranch } from '@/interfaces/IBranch';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import type { IStoreSetupStatus } from '@/interfaces/IStoreSetup';
  import type { IUser } from '@/interfaces/IUser';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { vueData } from '@/VueData';

  /**
   * Step 4 · «Pedidos» (plan §4.8): how customers order (tables, pickup,
   * delivery, or only the menu for now), the delivery fee and reach, the
   * payment methods and where orders arrive, with a live preview on wide
   * screens. «Guardar y terminar» saves everything once (`PUT …/orders`; a
   * second save answering `ORDERS_ALREADY_SET` counts as saved); «Configurar
   * después» moves on without touching the branch. From the dashboard
   * checklist (`checklistMode`) there is no «Configurar después». Exposes the
   * screen handle of the page's bottom bar.
   */
  const props = defineProps<{
    restaurant: IRestaurant;
    /** The branch the setup state reports (`status.ordering.branchId`). */
    branch: IBranch;
    status: IStoreSetupStatus;
    user: IUser;
    /** Right after the first publish: «Su menú ya está publicado». */
    fromPublish: boolean;
    checklistMode: boolean;
    wide: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'saved'): void;
    (e: 'skipped'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const online = useOnline();
  const ui = useUi();
  const gtag = useGtag();
  const restaurantStore = useRestaurantStore();
  const branchesService = new BranchesService();

  const form = ref<IOrdersForm>(
    initialOrdersForm(
      props.restaurant.businessType ?? null,
      props.user?.whatsapp ?? null
    )
  );
  /** `PUT …/orders` is running. */
  const saving = ref(false);
  /** «Configurar después» is running. */
  const skipping = ref(false);
  /** Saved or skipped: the page is moving on; nothing can be sent again. */
  const finished = ref(false);
  let alive = true;

  //#region Derived
  const country = computed(() => props.restaurant.country);
  const ctx = computed<IOrdersContext>(() => ({
    hasZones: props.status.ordering?.hasZones ?? false,
    country: country.value,
  }));
  const blocker = computed(() => ordersBlocker(form.value, ctx.value));
  const busy = computed(() => saving.value || skipping.value);
  const locked = computed(() => busy.value || finished.value);
  const subtitle = computed(() =>
    props.status.ordering?.hasHours === false
      ? tr('storeSetup.orders.subtitleNoHours')
      : tr('storeSetup.orders.subtitle')
  );
  const center = computed(() => ({
    lat: props.branch.lat,
    lng: props.branch.lon,
  }));
  const logoUrl = computed(() =>
    props.restaurant.logo
      ? `${Globals.uploadsPath}/${props.restaurant.logo}`
      : null
  );
  const initials = computed(() => storeInitials(props.restaurant.name));
  /** The store's look as the API resolved it (saved → legacy → logo → name, D-ADM-5). */
  const brand = computed(() =>
    defaultBrand({
      savedBrand: props.restaurant.storefrontStyle?.brand,
      name: props.restaurant.name,
    })
  );
  const todayHours = computed(() => {
    const week = branchWeek(props.branch.hourRanges);
    return week ? todayHoursLabel(week, new Date()) : null;
  });
  const preview = computed(() =>
    ordersPreviewModel(form.value, props.restaurant.currency, country.value)
  );
  //#endregion

  //#region Bottom bar
  // From the checklist there is nothing to leave for later; offline it would not save.
  const hasLater = computed(() => !props.checklistMode && online.value);
  const helperKey = computed(() =>
    ordersHelperKey(blocker.value, hasLater.value)
  );
  const bar = computed<IFlowBarState>(() =>
    flowBar({
      leftLabel: hasLater.value ? tr('storeSetup.orders.later') : null,
      helper: helperKey.value === null ? null : tr(helperKey.value),
      primaryLabel: props.checklistMode
        ? tr('storeSetup.orders.saveShort')
        : tr('storeSetup.orders.save'),
      primaryDisabled:
        blocker.value !== null || !online.value || skipping.value,
      primaryBusy: saving.value || finished.value,
    })
  );

  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** Every loaded copy of a branch: merged, never replaced (other screens read fields an answer may lack). */
  function branchCopies(branchId: number): (IBranch | null | undefined)[] {
    return [
      restaurantStore.branches?.find((b) => b.id === branchId),
      vueData.allBranches?.find((b) => b.id === branchId),
      vueData.branch,
    ];
  }

  /** The branch as saved before (`ORDERS_ALREADY_SET`) into the local copies; best effort. */
  async function reloadBranchCopies(): Promise<void> {
    try {
      const saved = await branchesService.getById(props.branch.id);
      mergeBranchUpdate(branchCopies(saved.id), saved);
    } catch (error) {
      console.warn(
        '[store-setup] branch reload after ORDERS_ALREADY_SET failed',
        error
      );
    }
  }

  /** «Guardar y terminar» / «Guardar». */
  async function primary() {
    if (locked.value || blocker.value !== null || !online.value) return;
    blurActive();
    const dto = toSetupOrdersDto(form.value, props.branch.id, ctx.value);
    saving.value = true;
    let saved = false;
    try {
      const response = await StoreSetupService.saveOrders(
        props.restaurant.id,
        dto
      );
      mergeBranchUpdate(branchCopies(response.branch.id), response.branch);
      gtag.event('orders_configured', {
        ...ordersConfiguredEvent(dto, props.checklistMode),
      });
      saved = true;
    } catch (error) {
      // Already saved (another device, or a retry after a lost answer): done,
      // with what was saved then (the page reloads the setup state and moves on).
      if (isOrdersAlreadySet(getServerErrorCode(error))) {
        await reloadBranchCopies();
        saved = true;
      } else {
        console.error(error);
        ui.showToast(AlertType.Error, tr('storeSetup.orders.saveError'));
      }
    }
    if (!alive) return;
    saving.value = false;
    if (!saved) return;
    finished.value = true;
    emit('saved');
  }

  /** «Configurar después»: tables and pickup as created, delivery off; the branch is not touched. */
  async function back() {
    if (props.checklistMode || locked.value || !online.value) return;
    blurActive();
    skipping.value = true;
    let skipped = false;
    try {
      await StoreSetupService.patch(props.restaurant.id, { stage: 'live' });
      gtag.event('orders_skipped', {});
      skipped = true;
    } catch (error) {
      console.error(error);
      ui.showToast(
        AlertType.Error,
        tr(
          setupErrorKey(getServerErrorCode(error)) ??
            'storeSetup.errors.generic'
        )
      );
    }
    if (!alive) return;
    skipping.value = false;
    if (!skipped) return;
    finished.value = true;
    emit('skipped');
  }

  /** The screen has no secondary action. */
  function nothing(): Promise<void> {
    return Promise.resolve();
  }
  //#endregion

  function patch(change: TOrdersFormPatch) {
    if (!locked.value) form.value = { ...form.value, ...change };
  }

  onBeforeUnmount(() => {
    alive = false;
  });

  defineExpose({ bar, busy, primary, secondary: nothing, back });
</script>

<template>
  <div class="v2-setup-step-orders" :class="wide ? 'is-wide' : 'is-narrow'">
    <div class="orders-main">
      <header class="step-head">
        <PosUiBadge
          v-if="fromPublish"
          class="published-badge"
          color="success"
          variant="soft"
          icon="check"
          :label="$t('storeSetup.orders.published')"
        />
        <h1 class="step-title" tabindex="-1">
          {{ $t('storeSetup.orders.title') }}
        </h1>
        <p class="step-subtitle">{{ subtitle }}</p>
      </header>
      <V2SetupOrderTypes :value="form" :disabled="locked" @patch="patch" />
      <V2SetupDeliveryCard
        :value="form"
        :center="center"
        :currency="restaurant.currency"
        :country="country"
        :has-zones="ctx.hasZones"
        :disabled="locked"
        @patch="patch"
      />
      <V2SetupPaymentChips
        v-show="!form.menuOnly"
        :value="form"
        :country="country"
        :disabled="locked"
        @patch="patch"
      />
      <V2SetupOrdersArrival
        v-show="!form.menuOnly"
        :value="form"
        :country="country"
        :disabled="locked"
        @patch="patch"
      />
    </div>
    <aside v-if="wide" class="orders-preview">
      <V2SetupStorePreview
        variant="orders"
        :brand="brand"
        :name="restaurant.name"
        :address="branch.address || ''"
        :logo-url="logoUrl"
        :initials="initials"
        :today-hours="todayHours"
        :orders="preview"
      />
    </aside>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-step-orders {
    @apply tw-mx-auto tw-w-full;
    &.is-narrow {
      @apply tw-max-w-2xl;
    }
    // Wide: the form beside a 22 rem sticky preview.
    &.is-wide {
      @apply tw-grid tw-max-w-5xl tw-items-start tw-gap-6;
      grid-template-columns: minmax(0, 1fr) 22rem;
    }
    .orders-main {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3;
    }
    .step-head {
      @apply tw-mb-1 tw-flex tw-flex-col tw-items-start tw-gap-1;
    }
    .published-badge {
      @apply tw-mb-1;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-bold tw-text-neutral-900 md:tw-text-2xl;
    }
    .step-subtitle {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500 md:tw-text-base;
    }
    .orders-preview {
      @apply tw-sticky tw-top-0 tw-min-w-0;
    }
  }
</style>
